import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    FormControlLabel,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { SectionLabel, PanelCard } from '../primitives';
import { apiFetch } from '../../lib/api';
import { SuburbField } from '../SuburbField';
import { InvoicingTab } from './InvoicingTab';
import type { Client, ClientContact, ClientDetail, Skillset, XeroOption } from '../../hooks/useClients';

/**
 * The client form: Details, Xero, Rates, Invoicing.
 *
 * The same four tabs as the legacy screen (clients_form_view_api.php:43-48),
 * except that there they were four separate page loads through four controllers,
 * each re-fetching the client. Here they are one form over one fetch.
 *
 * Invoicing reads the client's invoices live from Xero, and so does the Xero
 * tab: opening it fills the five invoice defaults from the Xero contact, the
 * way the legacy Xero screen did (xero_form_view.php called api/getContact on
 * open). They arrive as unsaved edits, so Update Client is what commits them.
 *
 * Renders the title, body and actions of a Dialog; the caller owns the <Dialog>.
 * Laid out like the Edit Job popup.
 */

const STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

/** GET /clients/:id/xero-defaults — the contact's sales defaults, live. */
interface XeroDefaultsReply {
    readonly state: 'not_configured' | 'not_connected' | 'not_linked' | 'not_found' | 'ok';
    readonly message: string | null;
    readonly defaults: {
        readonly dueDays: number | null;
        readonly dueId: number | null;
        readonly amountsAreId: number | null;
        readonly salesGstId: number | null;
        readonly salesAccount: string | null;
        readonly contactName: string | null;
        /** Codes Xero sent that this app has no option row for. */
        readonly unknownCodes: readonly string[];
    } | null;
}

interface ClientFormProps {
    readonly detail: ClientDetail;
    readonly xeroOptions: readonly XeroOption[];
    readonly skillsets: readonly Skillset[];
    /** 3 opens on Invoicing, for the link back from Create Invoice. */
    readonly initialTab?: number;
    readonly onSaved: () => void;
    readonly onDeleted: () => void;
    readonly onClose: () => void;
}

type FieldMap = Partial<Record<keyof Client, string>>;

function text(v: string | null | undefined): string {
    return v ?? '';
}

export function ClientForm({
    detail,
    xeroOptions,
    skillsets,
    initialTab = 0,
    onSaved,
    onDeleted,
    onClose,
}: ClientFormProps): React.JSX.Element {
    const [tab, setTab] = useState(initialTab);
    const [form, setForm] = useState<FieldMap>({});
    const [isActive, setIsActive] = useState(detail.client.isActive);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [xeroLive, setXeroLive] = useState<XeroDefaultsReply | null>(null);
    const [xeroError, setXeroError] = useState<string | null>(null);

    // Reset whenever a different client is opened, or the same one reloads —
    // otherwise the previous client's edits bleed into the next form.
    useEffect(() => {
        setForm({});
        setIsActive(detail.client.isActive);
        setError(null);
        setConfirmDelete(false);
        setXeroLive(null);
        setXeroError(null);
    }, [detail]);

    /**
     * Read the contact's real settings out of Xero when the tab is opened, as
     * the legacy screen did. Once per client per open — a re-read on every tab
     * switch would undo edits made and not yet saved.
     */
    const clientId = detail.client.id;
    const xeroContactId = detail.client.xeroContactId;
    useEffect(() => {
        if (tab !== 1 || xeroLive !== null || xeroError !== null) {
            return;
        }
        let cancelled = false;
        apiFetch<XeroDefaultsReply>(`/clients/${String(clientId)}/xero-defaults`)
            .then((r) => {
                if (cancelled) {
                    return;
                }
                setXeroLive(r);
                if (r.state !== 'ok' || r.defaults === null) {
                    return;
                }
                const d = r.defaults;
                // Xero's values, as if they had just been typed: anything the
                // user already changed in this session wins, and nothing is
                // written until Update Client.
                setForm((f) => {
                    const next: FieldMap = { ...f };
                    const put = (key: keyof Client, v: string | number | null): void => {
                        if (next[key] === undefined && v !== null) {
                            next[key] = String(v);
                        }
                    };
                    put('xeroDueDays', d.dueDays);
                    put('xeroDueId', d.dueId);
                    put('xeroAmountsAreId', d.amountsAreId);
                    put('xeroSalesGstId', d.salesGstId);
                    put('xeroSalesAccount', d.salesAccount);
                    // Only count as an edit what actually differs from what is
                    // stored, so an unchanged client does not look dirty.
                    for (const key of Object.keys(next) as (keyof Client)[]) {
                        if (f[key] === undefined && next[key] === text(detail.client[key] as string | null)) {
                            delete next[key];
                        }
                    }
                    return next;
                });
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setXeroError(err instanceof Error ? err.message : 'Could not read this client from Xero');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [tab, clientId, xeroContactId, xeroLive, xeroError, detail.client]);

    const value = (key: keyof Client): string => form[key] ?? text(detail.client[key] as string | null);

    const set = (key: keyof Client) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
    };

    const dirty = Object.keys(form).length > 0 || isActive !== detail.client.isActive;

    function save(): void {
        setSaving(true);
        setError(null);
        // Only what changed. A PATCH of the whole object would let the Xero tab
        // overwrite the Details tab with stale values from its own render.
        const body: Record<string, unknown> = { ...form };
        if (isActive !== detail.client.isActive) {
            body['isActive'] = isActive;
        }
        for (const numeric of ['xeroDueDays', 'xeroDueId', 'xeroAmountsAreId', 'xeroSalesGstId']) {
            if (typeof body[numeric] === 'string') {
                body[numeric] = body[numeric] === '' ? null : Number(body[numeric]);
            }
        }

        apiFetch<Client>(`/clients/${String(detail.client.id)}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        })
            .then(() => {
                onSaved();
                onClose();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function remove(): void {
        setSaving(true);
        apiFetch<unknown>(`/clients/${String(detail.client.id)}`, { method: 'DELETE' })
            .then(onDeleted)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not delete');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const byKind = useMemo(() => {
        const out: Record<string, XeroOption[]> = {};
        for (const o of xeroOptions) {
            (out[o.kind] ??= []).push(o);
        }
        return out;
    }, [xeroOptions]);

    return (
        <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                Edit Client
                <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                    {detail.client.companyName}
                </Typography>
                <Chip
                    size="small"
                    label={isActive ? 'Active' : 'Disabled'}
                    color={isActive ? 'success' : 'default'}
                    variant="outlined"
                />
                <Box sx={{ flex: 1 }} />
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={isActive}
                            onChange={(e) => {
                                setIsActive(e.target.checked);
                            }}
                        />
                    }
                    label={<Typography sx={{ fontSize: '0.8rem' }}>Active</Typography>}
                />
                <IconButton onClick={onClose} size="small" disabled={saving} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ minHeight: '60vh' }}>
                {error !== null ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                <Tabs
                    value={tab}
                    onChange={(_e, v: number) => {
                        setTab(v);
                    }}
                    sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                >
                    <Tab label="Details" />
                    <Tab label="Xero" />
                    <Tab label={`Rates (${String(detail.rates.length)})`} />
                    <Tab label="Invoicing" />
                </Tabs>

                {/* ─── Details ────────────────────────────────────────────────── */}
                {tab === 0 ? (
                    <Stack gap={2.5}>
                        <PanelCard>
                            <SectionLabel>Company</SectionLabel>
                            <Stack gap={1.5}>
                                <TextField
                                    label="Company name"
                                    size="small"
                                    required
                                    value={value('companyName')}
                                    onChange={set('companyName')}
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <TextField
                                        label="ABN"
                                        size="small"
                                        fullWidth
                                        value={value('abn')}
                                        onChange={set('abn')}
                                        helperText="11 digits"
                                    />
                                    <TextField
                                        label="Phone"
                                        size="small"
                                        fullWidth
                                        value={value('phoneNumber')}
                                        onChange={set('phoneNumber')}
                                    />
                                </Stack>
                            </Stack>
                        </PanelCard>

                        <PanelCard>
                            <SectionLabel>Account contact</SectionLabel>
                            <Stack gap={1.5}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <TextField
                                        label="First name"
                                        size="small"
                                        fullWidth
                                        value={value('firstName')}
                                        onChange={set('firstName')}
                                    />
                                    <TextField
                                        label="Last name"
                                        size="small"
                                        fullWidth
                                        value={value('lastName')}
                                        onChange={set('lastName')}
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <TextField
                                        label="Email"
                                        size="small"
                                        fullWidth
                                        value={value('emailAddress')}
                                        onChange={set('emailAddress')}
                                    />
                                    <TextField
                                        label="Direct phone"
                                        size="small"
                                        fullWidth
                                        value={value('directPhoneNumber')}
                                        onChange={set('directPhoneNumber')}
                                    />
                                </Stack>
                            </Stack>
                        </PanelCard>

                        <PanelCard>
                            <SectionLabel>Address</SectionLabel>
                            <Stack gap={1.5}>
                                <TextField
                                    label="Street"
                                    size="small"
                                    value={value('streetName')}
                                    onChange={set('streetName')}
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <SuburbField
                                        value={value('suburbName')}
                                        onChange={(v) => {
                                            setForm((f) => ({ ...f, suburbName: v }));
                                        }}
                                        onSelect={(m) => {
                                            setForm((f) => ({
                                                ...f,
                                                suburbName: m.suburb,
                                                postcode: m.postcode,
                                                state: m.state,
                                            }));
                                        }}
                                    />
                                    <TextField
                                        label="Postcode"
                                        size="small"
                                        sx={{ width: { sm: 140 } }}
                                        value={value('postcode')}
                                        onChange={set('postcode')}
                                    />
                                    {/* The legacy free-text state column held both 'VIC'
                                    and 'Vic'; a select is why that cannot recur. */}
                                    <TextField
                                        select
                                        label="State"
                                        size="small"
                                        sx={{ width: { sm: 120 } }}
                                        value={value('state')}
                                        onChange={set('state')}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {STATES.map((s) => (
                                            <MenuItem key={s} value={s}>
                                                {s}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                            </Stack>
                        </PanelCard>

                        <ContactsPanel clientId={detail.client.id} contacts={detail.contacts} onChanged={onSaved} />
                    </Stack>
                ) : null}

                {/* ─── Xero ───────────────────────────────────────────────────── */}
                {tab === 1 ? (
                    <Stack gap={2.5}>
                        <XeroDefaultsBanner live={xeroLive} error={xeroError} />

                        <PanelCard>
                            <SectionLabel>Invoice defaults</SectionLabel>
                            <Stack gap={1.5}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <TextField
                                        label="Due (days)"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        value={value('xeroDueDays')}
                                        onChange={set('xeroDueDays')}
                                    />
                                    <TextField
                                        select
                                        label="Due terms"
                                        size="small"
                                        fullWidth
                                        value={value('xeroDueId')}
                                        onChange={set('xeroDueId')}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {(byKind['due'] ?? []).map((o) => (
                                            <MenuItem key={o.id} value={String(o.id)}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                    <TextField
                                        select
                                        label="Amounts are"
                                        size="small"
                                        fullWidth
                                        value={value('xeroAmountsAreId')}
                                        onChange={set('xeroAmountsAreId')}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {(byKind['amounts_are'] ?? []).map((o) => (
                                            <MenuItem key={o.id} value={String(o.id)}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        label="Sales GST"
                                        size="small"
                                        fullWidth
                                        value={value('xeroSalesGstId')}
                                        onChange={set('xeroSalesGstId')}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {(byKind['sales_gst'] ?? []).map((o) => (
                                            <MenuItem key={o.id} value={String(o.id)}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                                <TextField
                                    label="Sales account code"
                                    size="small"
                                    value={value('xeroSalesAccount')}
                                    onChange={set('xeroSalesAccount')}
                                    helperText="Xero chart-of-accounts code, e.g. 200"
                                />
                            </Stack>
                        </PanelCard>

                        <PanelCard>
                            <SectionLabel>Xero link</SectionLabel>
                            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>Xero ContactID</Typography>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mt: 0.5 }}>
                                {detail.client.xeroContactId ?? 'Not linked'}
                            </Typography>
                        </PanelCard>
                    </Stack>
                ) : null}

                {/* ─── Rates ──────────────────────────────────────────────────── */}
                {tab === 2 ? (
                    <RatesPanel
                        clientId={detail.client.id}
                        rates={detail.rates}
                        skillsets={skillsets}
                        onChanged={onSaved}
                    />
                ) : null}

                {/* ─── Invoicing ──────────────────────────────────────────────── */}
                {tab === 3 ? (
                    <InvoicingTab clientId={detail.client.id} companyName={detail.client.companyName} />
                ) : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {confirmDelete ? (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mr: 'auto' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: 'error.main' }}>Delete this client?</Typography>
                        <Button size="small" color="error" variant="contained" onClick={remove} disabled={saving}>
                            Delete
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setConfirmDelete(false);
                            }}
                        >
                            Keep
                        </Button>
                    </Stack>
                ) : (
                    <Button
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => {
                            setConfirmDelete(true);
                        }}
                        disabled={saving}
                        sx={{ mr: 'auto' }}
                    >
                        Delete Client
                    </Button>
                )}
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={!dirty || saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                    {saving ? 'Saving...' : 'Update Client'}
                </Button>
            </DialogActions>
        </>
    );
}

// ─── Xero tab banner ────────────────────────────────────────────────────────

/**
 * What the Xero read did, in one line above the fields.
 *
 * The fields below it are the same either way: what Xero says when it answered,
 * what is stored here when it could not. Saying which is the whole point of the
 * banner — the legacy screen filled the form from Xero and said nothing, so a
 * blank dropdown looked the same as a dropdown Xero had emptied.
 */
function XeroDefaultsBanner({
    live,
    error,
}: {
    readonly live: XeroDefaultsReply | null;
    readonly error: string | null;
}): React.JSX.Element {
    if (error !== null) {
        return (
            <Alert severity="error">
                {error} The values below are the ones stored here; editing and saving still works.
            </Alert>
        );
    }
    if (live === null) {
        return (
            <Alert severity="info" icon={<CircularProgress size={16} />}>
                Reading this client&apos;s invoice defaults from Xero…
            </Alert>
        );
    }
    if (live.state === 'not_configured') {
        return (
            <Alert severity="warning">
                Xero is not set up for the new portal yet, so these are the values stored here. The Xero app&apos;s
                client ID and secret need adding to the API.
            </Alert>
        );
    }
    if (live.state === 'not_connected') {
        return (
            <Alert severity="info">
                {live.message ?? 'Xero is not connected.'} These are the values stored here. Connect Xero on the
                Invoicing tab to read the live ones.
            </Alert>
        );
    }
    if (live.state === 'not_linked') {
        return (
            <Alert severity="info">
                This client has no Xero ContactID, so these are the values stored here rather than Xero&apos;s.
            </Alert>
        );
    }
    if (live.state === 'not_found') {
        return (
            <Alert severity="warning">
                {live.message ?? 'Xero does not have this contact.'} These are the values stored here.
            </Alert>
        );
    }
    const unknown = live.defaults?.unknownCodes ?? [];
    return (
        <Alert severity="success">
            Filled from Xero{live.defaults?.contactName === null || live.defaults?.contactName === undefined
                ? ''
                : ` — ${live.defaults.contactName}`}
            . Update Client to keep them here; anything you change first wins.
            {unknown.length > 0 ? ` Xero sent settings this portal has no option for: ${unknown.join(', ')}.` : ''}
        </Alert>
    );
}

// ─── Contacts ───────────────────────────────────────────────────────────────

interface ContactsPanelProps {
    readonly clientId: number;
    readonly contacts: readonly ClientContact[];
    readonly onChanged: () => void;
}

/**
 * The contact list from the Details tab.
 *
 * The legacy version posted straight to an INSERT with no validation
 * (save_client_contact_detail), and wrote the client's company NAME onto every
 * contact row alongside the id. Only the id is stored now.
 */
function ContactsPanel({ clientId, contacts, onChanged }: ContactsPanelProps): React.JSX.Element {
    const [editing, setEditing] = useState<number | 'new' | null>(null);
    const [draft, setDraft] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    function open(contact: ClientContact | null): void {
        setError(null);
        if (contact === null) {
            setEditing('new');
            setDraft({});
            return;
        }
        setEditing(contact.id);
        setDraft({
            contactName: contact.contactName,
            jobTitle: text(contact.jobTitle),
            division: text(contact.division),
            phoneNumber: text(contact.phoneNumber),
            phoneNumber2: text(contact.phoneNumber2),
            emailAddress: text(contact.emailAddress),
        });
    }

    function save(): void {
        setBusy(true);
        setError(null);
        const isNew = editing === 'new';
        const path = isNew ? `/clients/${String(clientId)}/contacts` : `/clients/contacts/${String(editing ?? 0)}`;
        apiFetch<unknown>(path, { method: isNew ? 'POST' : 'PATCH', body: JSON.stringify(draft) })
            .then(() => {
                setEditing(null);
                onChanged();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save the contact');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    function remove(id: number): void {
        setBusy(true);
        apiFetch<unknown>(`/clients/contacts/${String(id)}`, { method: 'DELETE' })
            .then(onChanged)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not remove the contact');
            })
            .finally(() => {
                setBusy(false);
            });
    }

    const field = (key: string, label: string): React.JSX.Element => (
        <TextField
            label={label}
            size="small"
            fullWidth
            value={draft[key] ?? ''}
            onChange={(e) => {
                setDraft((d) => ({ ...d, [key]: e.target.value }));
            }}
        />
    );

    return (
        <PanelCard>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <SectionLabel divider={false}>Contacts</SectionLabel>
                </Box>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        open(null);
                    }}
                >
                    Add contact
                </Button>
            </Stack>

            {error !== null ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            ) : null}

            {contacts.length === 0 && editing === null ? (
                <Typography sx={{ fontSize: '0.82rem', color: 'text.disabled' }}>
                    No contacts recorded for this client.
                </Typography>
            ) : null}

            {contacts.length > 0 ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Job title</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell align="right" />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {contacts.map((ct) => (
                            <TableRow key={ct.id} hover>
                                <TableCell sx={{ fontWeight: 600 }}>{ct.contactName}</TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{ct.jobTitle}</TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>
                                    {[ct.phoneNumber, ct.phoneNumber2]
                                        .filter((p): p is string => p !== null && p !== '')
                                        .join(' · ')}
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>{ct.emailAddress}</TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            open(ct);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={busy}
                                        onClick={() => {
                                            remove(ct.id);
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : null}

            {editing !== null ? (
                <Box sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'primary.main', borderRadius: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 1.5 }}>
                        {editing === 'new' ? 'New contact' : 'Edit contact'}
                    </Typography>
                    <Stack gap={1.5}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            {field('contactName', 'Name')}
                            {field('jobTitle', 'Job title')}
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            {field('phoneNumber', 'Phone')}
                            {field('phoneNumber2', 'Second phone')}
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            {field('emailAddress', 'Email')}
                            {field('division', 'Division')}
                        </Stack>
                        <Stack direction="row" gap={1}>
                            <Button variant="contained" size="small" onClick={save} disabled={busy}>
                                Save contact
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    setEditing(null);
                                }}
                            >
                                Cancel
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            ) : null}
        </PanelCard>
    );
}

// ─── Rates ──────────────────────────────────────────────────────────────────

interface RatesPanelProps {
    readonly clientId: number;
    readonly rates: readonly ClientDetail['rates'][number][];
    readonly skillsets: readonly Skillset[];
    readonly onChanged: () => void;
}

/**
 * Charge-out rate per skillset.
 *
 * One row per skillset, always — set ones show their rate, unset ones show
 * blank. That is why there is no "add rate" button: the set of skillsets is
 * fixed, so adding was only ever a way to end up with two rates for one
 * skillset and no rule for which applied.
 */
/** Drop one key. The rest-destructuring form leaves an unused binding behind. */
function without(source: Record<number, string>, key: number): Record<number, string> {
    const copy = { ...source };
    delete copy[key];
    return copy;
}

function RatesPanel({ clientId, rates, skillsets, onChanged }: RatesPanelProps): React.JSX.Element {
    const [drafts, setDrafts] = useState<Record<number, string>>({});
    const [busy, setBusy] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const rateOf = useMemo(() => {
        const m = new Map<number, ClientDetail['rates'][number]>();
        for (const r of rates) {
            m.set(r.staffTitleId, r);
        }
        return m;
    }, [rates]);

    function commit(staffTitleId: number): void {
        const raw = drafts[staffTitleId];
        if (raw === undefined) {
            return;
        }
        const existing = rateOf.get(staffTitleId);

        if (raw.trim() === '') {
            if (existing === undefined) {
                return;
            }
            setBusy(staffTitleId);
            apiFetch<unknown>(`/clients/rates/${String(existing.id)}`, { method: 'DELETE' })
                .then(() => {
                    setDrafts((d) => without(d, staffTitleId));
                    onChanged();
                })
                .catch((err: unknown) => {
                    setError(err instanceof Error ? err.message : 'Could not remove the rate');
                })
                .finally(() => {
                    setBusy(null);
                });
            return;
        }

        const parsed = Number(raw);
        if (!Number.isFinite(parsed) || parsed < 0) {
            setError('Rate must be a number of 0 or more');
            return;
        }
        setBusy(staffTitleId);
        setError(null);
        apiFetch<unknown>(`/clients/${String(clientId)}/rates`, {
            method: 'PUT',
            body: JSON.stringify({ staffTitleId, ratePerHour: parsed }),
        })
            .then(() => {
                setDrafts((d) => without(d, staffTitleId));
                onChanged();
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not save the rate');
            })
            .finally(() => {
                setBusy(null);
            });
    }

    return (
        <PanelCard>
            <SectionLabel>Charge-out rates</SectionLabel>
            {error !== null ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                    {error}
                </Alert>
            ) : null}

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Skillset</TableCell>
                        <TableCell sx={{ width: 170 }}>Rate / hour</TableCell>
                        <TableCell sx={{ width: 130 }}>Cost / hour</TableCell>
                        <TableCell sx={{ width: 110 }} align="right">
                            Margin
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {skillsets.map((s) => {
                        const existing = rateOf.get(s.id);
                        const shown = drafts[s.id] ?? (existing === undefined ? '' : String(existing.ratePerHour));
                        const charge = Number(shown);
                        // Margin against the skillset's hire rate, which the legacy
                        // screen never showed — the two numbers lived on different
                        // pages, so nobody could see a rate set below cost.
                        const margin =
                            s.hireRate !== null && s.hireRate > 0 && Number.isFinite(charge) && shown !== ''
                                ? ((charge - s.hireRate) / s.hireRate) * 100
                                : null;
                        return (
                            <TableRow key={s.id} hover>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Box
                                            sx={{
                                                width: 9,
                                                height: 9,
                                                borderRadius: '50%',
                                                bgcolor: s.colour ?? 'text.disabled',
                                            }}
                                        />
                                        {s.title}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        placeholder="—"
                                        value={shown}
                                        disabled={busy === s.id}
                                        onChange={(e) => {
                                            setDrafts((d) => ({ ...d, [s.id]: e.target.value }));
                                        }}
                                        onBlur={() => {
                                            commit(s.id);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                commit(s.id);
                                            }
                                        }}
                                        slotProps={{ input: { startAdornment: <Box sx={{ mr: 0.5 }}>$</Box> } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ color: 'text.secondary' }}>
                                    {s.hireRate === null ? '—' : `$${s.hireRate.toFixed(2)}`}
                                </TableCell>
                                <TableCell align="right">
                                    {margin === null ? (
                                        <Typography sx={{ color: 'text.disabled', fontSize: '0.8rem' }}>—</Typography>
                                    ) : (
                                        <Tooltip title="Charge-out against the skillset's hire rate">
                                            <Typography
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 700,
                                                    color: margin < 0 ? 'error.main' : 'success.main',
                                                }}
                                            >
                                                {margin >= 0 ? '+' : ''}
                                                {margin.toFixed(0)}%
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', mt: 1.5 }}>
                Blank a rate to remove it. Changes save when you leave the field.
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle', mr: 0.5 }} />
                Cost per hour comes from the skillset, on the Skillsets screen.
            </Typography>
        </PanelCard>
    );
}
